// Connection-lost redirect helpers.
//
// When the API is unreachable we must NOT treat it as "session expired" — the
// tokens are still valid, the server just isn't answering (dev restart, deploy,
// dropped wifi). Instead we park the user on /connection-lost with their tokens
// intact and send them back where they were once the API responds again.
//
// Plain module (no React) so non-component code — the RTK base query, the auth
// boot check — can trigger the redirect too.

import { STANDALONE_PATHS } from "@/routes/common/standalone-paths";

const RETURN_TO_KEY = "hrm.connection-lost.return-to";

// sessionStorage throws in some privacy modes — never let that break the redirect.
const safeSession = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export const isOnConnectionLostPage = (): boolean =>
  typeof window !== "undefined" &&
  window.location.pathname === STANDALONE_PATHS.CONNECTION_LOST;

/**
 * Park the user on the connection-lost screen, remembering the current URL.
 * Hard navigation on purpose: when the API comes back the app re-boots clean
 * instead of resuming from half-failed RTK Query state.
 */
export const goToConnectionLost = (): void => {
  if (typeof window === "undefined" || isOnConnectionLostPage()) return;

  const { pathname, search, hash } = window.location;
  // Auth screens have nothing to come back to — send them home afterwards.
  if (!pathname.startsWith("/auth")) {
    safeSession.set(RETURN_TO_KEY, `${pathname}${search}${hash}`);
  }
  window.location.replace(STANDALONE_PATHS.CONNECTION_LOST);
};

/** Where to send the user once the API answers again. Never leaves the app. */
export const getConnectionLostReturnPath = (): string => {
  const stored = safeSession.get(RETURN_TO_KEY);
  // Must be a same-origin absolute path ("//host" would navigate off-site).
  if (!stored || !stored.startsWith("/") || stored.startsWith("//")) return "/";
  if (stored.startsWith(STANDALONE_PATHS.CONNECTION_LOST)) return "/";
  return stored;
};

export const clearConnectionLostReturnPath = (): void =>
  safeSession.remove(RETURN_TO_KEY);
